import {ChangeDetectorRef, Component, inject, input, OnInit, signal, viewChild} from '@angular/core'
import {FormGroup} from '@angular/forms'
import {MatButton} from '@angular/material/button'
import {MatError} from '@angular/material/form-field'
import {MatStepperPrevious} from '@angular/material/stepper'
import {Router} from '@angular/router'
import {BankIdComponent} from '@sparbanken-syd/sparbanken-syd-bankid'
import {ApplicationSummaryComponent, SummarySection} from '@sparbanken-syd/sparbanken-syd-theme'
import {AccountApplication} from '@sparbanken-syd/spb-savings-account-backend'
import {finalize, tap} from 'rxjs'
import {environment} from '../../../environments/environment'
import {OrientationService} from '../../services/orientation.service'
import {RouteService} from '../../services/route.service'
import {SavingsAccountService} from '../../services/savings-account.service'
import {SummaryService} from '../../services/summary.service'

@Component({
  selector: 'spb-summary',
  templateUrl: './summary.component.html',
  styleUrls: ['./summary.component.scss'],
  imports: [BankIdComponent, MatError, MatStepperPrevious, ApplicationSummaryComponent, MatButton]
})
export class SummaryComponent implements OnInit {
  public bankId = viewChild(BankIdComponent)
  public savingsAccountForm = input.required<FormGroup>()
  public stepperTrigger = input.required<boolean>()

  public summarySections = signal<SummarySection[]>([])
  public readonly orientationService = inject(OrientationService)

  /**
   * Show an error message.
   */
  public errorMessage = ''

  public accountApplication = {id: ''} as AccountApplication

  /**
   * Controls visibility of signing process
   */
  public signingHidden = signal(false)
  public signingInProgress = signal(false)

  protected readonly requestApi = environment.requestApi
  protected readonly authServiceUrl = environment.authServiceUrl
  protected readonly domain = environment.domain
  protected readonly savingsAccountService = inject(SavingsAccountService)
  protected readonly routeService = inject(RouteService)
  private readonly summaryService = inject(SummaryService)
  private readonly router = inject(Router)
  private readonly ref = inject(ChangeDetectorRef)

  public ngOnInit(): void {
    this.savingsAccountForm().valueChanges.subscribe(() => {
      this.summarySections
        .set(this.summaryService.createSummary(this.savingsAccountForm()))
    })
  }

  /**
   * Start the signing process - saves request first, then initiates BankID
   */
  public startSigning(): void {
    this.errorMessage = ''
    this.signingInProgress.set(true)

    this.saveRequest().pipe(
      tap(() => {
        // Update UI state after successful save
        this.signingHidden.set(true)
        this.ref.detectChanges()
      }),
      finalize(() => {
        // Reset signing progress state regardless of outcome
        this.signingInProgress.set(false)
      })
    ).subscribe({
      next: () => {
        // Start BankID process after successful save
        this.bankId()?.startProcess()
      },
      error: () => {
        this.errorMessage = 'Ett fel uppstod vid ansökan. Vänligen försök igen.'
        this.signingHidden.set(false)
      }
    })
  }

  /**
   * Cancel BankID signing when idle and dialog is hidden
   */
  public cancelSigning(event: string): void {
    if (event === 'idle' && this.signingHidden()) {
      // Reset signing states
      this.signingInProgress.set(false)
      this.signingHidden.set(false)
      this.bankId()?.reset()
    }
  }

  /**
   * Submit signature after BankID completion
   */
  public submitSignature(token: string): void {
    this.errorMessage = ''
    this.signingInProgress.set(true)

    // Get information from received token
    const payload = JSON.parse(atob(token.split('.')[1]))

    // Check that signing personnummer is the same as application's personnummer.
    if (payload.sub !== this.accountApplication.applicantData.personalNumber.replace('-', '')) {
      // Show error message and reset process
      this.errorMessage = 'Personnumret i ansökan stämmer inte med signaturen'
      this.cancelSigning('idle')
      return
    }

    this.savingsAccountService.addSignature(token, this.accountApplication.id)
      .pipe(
        tap(() => {
          this.accountApplication.id = ''
        }),
        finalize(() => {
          this.signingInProgress.set(false)
        })
      )
      .subscribe(() => void this.router.navigate(['/complete'],
        {state: {accountApplication: this.accountApplication}})
      )
  }


  /**
   * Save the form (before the customer signs it)
   */
  private saveRequest() {
    const request: AccountApplication = this.savingsAccountForm().getRawValue()

    if (this.routeService.onHorseRoute()) {
      const bankRel = request.kycForm.bankRelationshipForm
      // Replace openSavingsAccount with openHorseAccount
      delete bankRel.openSavingsAccount
      bankRel.openHorseAccount = true
    }

    return this.savingsAccountService.saveRequest(request).pipe(
      tap((accountApplication) => {
        this.accountApplication = accountApplication
        this.savingsAccountForm().controls.id.setValue(this.accountApplication.id)
      })
    )
  }
}